import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Tabs,
  Tab,
  Button,
  TextField,
  Paper,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import PageHeader from '../components/common/PageHeader';
import TipTapEditor from '../components/common/TipTapEditor';
import LookupTable from '../components/common/LookupTable';
import ConfirmDialog from '../components/common/ConfirmDialog';
import { useNotification } from '../hooks/NotificationContext';
import api from '../services/api';
import {
  monitoringServicesService,
  type MonitoringService as MonitoringServiceType,
} from '../services/monitoringServicesService';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index }) => (
  <div role="tabpanel" hidden={value !== index}>
    {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
  </div>
);

const DebugWartungPage: React.FC = () => {
  const { showSuccess, showError } = useNotification();
  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(false);

  // E-Mail Debug state
  const [emailTo, setEmailTo] = useState('');
  const [emailSubject, setEmailSubject] = useState('');
  const [emailBody, setEmailBody] = useState('');
  const [emailSending, setEmailSending] = useState(false);
  const [emailDebugLog, setEmailDebugLog] = useState<string[]>([]);

  // Monitoring-Services state
  const [monitoringServices, setMonitoringServices] = useState<MonitoringServiceType[]>([]);
  const [monitoringServicesLoading, setMonitoringServicesLoading] = useState(true);
  const [monitoringServicesDialogOpen, setMonitoringServicesDialogOpen] = useState(false);
  const [editingMonitoringService, setEditingMonitoringService] = useState<MonitoringServiceType | null>(null);
  const [monitoringServiceName, setMonitoringServiceName] = useState('');
  const [monitoringServiceUrl, setMonitoringServiceUrl] = useState('');
  const [monitoringServiceApiKey, setMonitoringServiceApiKey] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<{ item: MonitoringServiceType; displayName: string } | null>(null);
  const [testLoading, setTestLoading] = useState(false);
  const [testResult, setTestResult] = useState<{ ok: boolean; message: string; environments?: string[] } | null>(null);

  const loadMonitoringServices = async () => {
    try {
      setMonitoringServicesLoading(true);
      const data = await monitoringServicesService.list();
      setMonitoringServices(data);
    } catch {
      // Error displayed by axios interceptor
    } finally {
      setMonitoringServicesLoading(false);
    }
  };

  useEffect(() => {
    loadMonitoringServices();
  }, []);

  const handleSendDebugEmail = async () => {
    if (!emailTo || !emailSubject) return;
    try {
      setEmailSending(true);
      setEmailDebugLog([]);
      const res = await api.post('/email/send-debug', {
        to: emailTo,
        subject: emailSubject,
        html_body: emailBody || '<p></p>',
      });
      setEmailDebugLog(res.data.debug_log || []);
      if (res.data.success) {
        showSuccess('E-Mail erfolgreich gesendet');
      } else {
        showError('E-Mail-Versand fehlgeschlagen – siehe Debug-Log');
      }
    } catch (err: any) {
      const detail = err?.response?.data?.detail || 'Fehler beim E-Mail-Versand';
      setEmailDebugLog((prev) => [...prev, `[ERROR] ${detail}`]);
    } finally {
      setEmailSending(false);
    }
  };

  const handleMonitoringServicesAdd = () => {
    setEditingMonitoringService(null);
    setMonitoringServiceName('');
    setMonitoringServiceUrl('');
    setMonitoringServiceApiKey('');
    setTestResult(null);
    setMonitoringServicesDialogOpen(true);
  };

  const handleMonitoringServicesEdit = (item: MonitoringServiceType) => {
    setEditingMonitoringService(item);
    setMonitoringServiceName(item.name);
    setMonitoringServiceUrl(item.url);
    setMonitoringServiceApiKey('');
    setTestResult(null);
    setMonitoringServicesDialogOpen(true);
  };

  const handleMonitoringServicesSave = async () => {
    try {
      setLoading(true);
      if (editingMonitoringService) {
        const payload: { name?: string; url?: string; api_key?: string } = {
          name: monitoringServiceName,
          url: monitoringServiceUrl.trim().replace(/\/+$/, ''),
        };
        if (monitoringServiceApiKey.trim()) payload.api_key = monitoringServiceApiKey;
        await monitoringServicesService.update(editingMonitoringService.id, payload);
        showSuccess('Monitoring-Service erfolgreich aktualisiert');
      } else {
        await monitoringServicesService.create({
          name: monitoringServiceName,
          url: monitoringServiceUrl.trim().replace(/\/+$/, ''),
          api_key: monitoringServiceApiKey,
        });
        showSuccess('Monitoring-Service erfolgreich angelegt');
      }
      setMonitoringServicesDialogOpen(false);
      await loadMonitoringServices();
    } catch {
      // Error displayed by axios interceptor
    } finally {
      setLoading(false);
    }
  };

  const handleMonitoringServicesDelete = (item: MonitoringServiceType) => {
    setItemToDelete({ item, displayName: item.name });
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!itemToDelete) return;
    try {
      setLoading(true);
      await monitoringServicesService.delete(itemToDelete.item.id);
      showSuccess('Monitoring-Service erfolgreich entfernt');
      await loadMonitoringServices();
      setDeleteDialogOpen(false);
      setItemToDelete(null);
    } catch {
      // Error displayed by axios interceptor
    } finally {
      setLoading(false);
    }
  };

  const handleTestInDialog = async () => {
    setTestResult(null);
    const url = monitoringServiceUrl.trim().replace(/\/+$/, '');
    if (!url) {
      showError('URL eingeben');
      return;
    }
    if (editingMonitoringService && !monitoringServiceApiKey.trim()) {
      try {
        setTestLoading(true);
        const res = await monitoringServicesService.testExistingService(editingMonitoringService.id, url);
        setTestResult(res);
        if (res.ok) showSuccess(res.message);
        else showError(res.message);
      } catch {
        // Error displayed by axios interceptor
      } finally {
        setTestLoading(false);
      }
      return;
    }
    const apiKey = monitoringServiceApiKey.trim();
    if (!apiKey) {
      showError('API-Key eingeben (oder bei Bearbeitung leer lassen, um gespeicherten Key zu testen)');
      return;
    }
    try {
      setTestLoading(true);
      const res = await monitoringServicesService.testConnection(url, apiKey);
      setTestResult(res);
      if (res.ok) showSuccess(res.message);
      else showError(res.message);
    } catch {
      // Error displayed by axios interceptor
    } finally {
      setTestLoading(false);
    }
  };

  const handleTestInTable = async (item: MonitoringServiceType) => {
    try {
      const res = await monitoringServicesService.testExistingService(item.id);
      if (res.ok) showSuccess(`${item.name}: ${res.message}`);
      else showError(`${item.name}: ${res.message}`);
    } catch {
      // Error displayed by axios interceptor
    }
  };

  return (
    <Box>
      <PageHeader
        title="Debug & Wartung"
        subtitle="Systemdiagnose, Monitoring-Services und Test-Tools"
        breadcrumbs={[{ label: 'Debug & Wartung' }]}
      />

      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs value={tabValue} onChange={(_, v) => setTabValue(v)} variant="scrollable" scrollButtons="auto">
          <Tab label="E-Mail Debug" />
          <Tab label="Monitoring-Services" />
        </Tabs>
      </Box>

      <TabPanel value={tabValue} index={0}>
        <Card sx={{ overflow: 'visible' }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              E-Mail Debug (Amazon SES)
            </Typography>
            <Alert severity="info" sx={{ mb: 3 }}>
              Hier kannst du Test-E-Mails über Amazon SES versenden. Die SES-Konfiguration wird aus der .env-Datei geladen.
            </Alert>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <TextField
                label="Empfänger"
                type="email"
                placeholder="empfänger@beispiel.de"
                value={emailTo}
                onChange={(e) => setEmailTo(e.target.value)}
                fullWidth
                size="small"
              />
              <TextField
                label="Betreff"
                placeholder="Test-E-Mail Betreff"
                value={emailSubject}
                onChange={(e) => setEmailSubject(e.target.value)}
                fullWidth
                size="small"
              />
              <Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Inhalt (HTML)
                </Typography>
                <TipTapEditor
                  content={emailBody}
                  onChange={(html) => setEmailBody(html)}
                  placeholder="E-Mail-Inhalt eingeben..."
                  minHeight={200}
                />
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                  variant="contained"
                  onClick={handleSendDebugEmail}
                  disabled={emailSending || !emailTo || !emailSubject}
                  startIcon={emailSending ? <CircularProgress size={20} /> : <SendIcon />}
                >
                  {emailSending ? 'Wird gesendet...' : 'E-Mail senden'}
                </Button>
              </Box>
            </Box>

            {emailDebugLog.length > 0 && (
              <Box sx={{ mt: 3 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Debug-Log
                </Typography>
                <Paper
                  variant="outlined"
                  sx={{
                    p: 2,
                    maxHeight: 400,
                    overflow: 'auto',
                    bgcolor: 'grey.900',
                    color: 'grey.100',
                  }}
                >
                  {emailDebugLog.map((line, idx) => (
                    <Typography
                      key={idx}
                      variant="body2"
                      sx={{
                        fontFamily: 'monospace',
                        fontSize: '0.8rem',
                        whiteSpace: 'pre-wrap',
                        lineHeight: 1.6,
                        color:
                          line.includes('FEHLER') || line.includes('ERROR')
                            ? 'error.light'
                            : line.includes('erfolgreich')
                              ? 'success.light'
                              : 'grey.100',
                      }}
                    >
                      {line}
                    </Typography>
                  ))}
                </Paper>
              </Box>
            )}
          </CardContent>
        </Card>
      </TabPanel>

      <TabPanel value={tabValue} index={1}>
        <Alert severity="info" sx={{ mb: 2 }}>
          Hier werden die <strong>Monitoring-Server</strong> verwaltet, die STARK-AIO abfragt. Die <strong>Umgebungen</strong> (z.B. kepp-app, stark-aio-prod) kommen aus der config.yaml des jeweiligen Monitoring-Services und erscheinen im Dashboard unter „Externe Systeme“.
        </Alert>
        <LookupTable
          items={monitoringServices}
          onAdd={handleMonitoringServicesAdd}
          onEdit={handleMonitoringServicesEdit}
          onDelete={handleMonitoringServicesDelete}
          onTest={handleTestInTable}
          loading={monitoringServicesLoading}
          columns={[
            { label: 'Name', render: (item) => item.name },
            {
              label: 'URL',
              render: (item) => (
                <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                  {item.url}
                </Typography>
              ),
            },
            { label: 'API-Key', render: (item) => item.api_key_masked },
          ]}
        />
      </TabPanel>

      {/* Monitoring-Services Dialog */}
      <Dialog open={monitoringServicesDialogOpen} onClose={() => setMonitoringServicesDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingMonitoringService ? 'Monitoring-Service bearbeiten' : 'Neuer Monitoring-Service'}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Name"
            placeholder="z.B. Server A, Lokal"
            fullWidth
            variant="outlined"
            value={monitoringServiceName}
            onChange={(e) => setMonitoringServiceName(e.target.value)}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label="URL"
            placeholder="http://localhost:8001 oder http://host.docker.internal:8001"
            fullWidth
            variant="outlined"
            value={monitoringServiceUrl}
            onChange={(e) => setMonitoringServiceUrl(e.target.value)}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label="API-Key"
            placeholder={editingMonitoringService ? 'Leer lassen = unverändert' : 'API-Key aus services/monitoring-service config.yaml'}
            fullWidth
            type="password"
            variant="outlined"
            value={monitoringServiceApiKey}
            onChange={(e) => setMonitoringServiceApiKey(e.target.value)}
            helperText={editingMonitoringService ? 'Nur ausfüllen um den API-Key zu ändern' : undefined}
          />
          {testResult && (
            <Alert
              severity={testResult.ok ? 'success' : 'error'}
              sx={{ mt: 2 }}
              onClose={() => setTestResult(null)}
            >
              {testResult.message}
              {testResult.environments && testResult.environments.length > 0 && (
                <Typography component="span" variant="body2" sx={{ display: 'block', mt: 1 }}>
                  Umgebungen: {testResult.environments.join(', ')}
                </Typography>
              )}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleTestInDialog}
            disabled={
              testLoading ||
              (editingMonitoringService
                ? false
                : !monitoringServiceUrl.trim() || !monitoringServiceApiKey.trim())
            }
            startIcon={testLoading ? <CircularProgress size={18} /> : null}
          >
            {testLoading ? 'Teste…' : 'Verbindung testen'}
          </Button>
          <Box sx={{ flex: 1 }} />
          <Button onClick={() => setMonitoringServicesDialogOpen(false)} disabled={loading}>
            Abbrechen
          </Button>
          <Button
            onClick={handleMonitoringServicesSave}
            variant="contained"
            disabled={
              loading ||
              !monitoringServiceName.trim() ||
              !monitoringServiceUrl.trim() ||
              (!editingMonitoringService && !monitoringServiceApiKey.trim())
            }
          >
            {loading ? <CircularProgress size={24} /> : 'Speichern'}
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={deleteDialogOpen}
        title="Löschen bestätigen"
        message={`Möchten Sie "${itemToDelete?.displayName}" wirklich löschen?`}
        onConfirm={handleDeleteConfirm}
        onCancel={() => {
          setDeleteDialogOpen(false);
          setItemToDelete(null);
        }}
      />
    </Box>
  );
};

export default DebugWartungPage;
